package or.garden.manager.features

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import karika.distribucija.ba.domain.model.ResultState
import kotlinx.browser.window
import or.garden.manager.components.BedModal
import or.garden.manager.components.ConfirmDeleteModal
import or.garden.manager.components.IrrigationModal
import or.garden.manager.components.Sidebar
import or.garden.manager.components.SnackbarHost
import or.garden.manager.components.SnackbarMessage
import or.garden.manager.components.SnackbarType
import or.garden.manager.domain.CropService
import or.garden.manager.domain.GardenHttpClient
import or.garden.manager.domain.GardenService
import or.garden.manager.domain.IrrigationService
import or.garden.manager.domain.NoteService
import or.garden.manager.domain.SeedService
import or.garden.manager.domain.model.ApiResponse
import or.garden.manager.domain.model.BedData
import or.garden.manager.domain.model.CreateIrrigationRequest
import or.garden.manager.domain.model.CropData
import or.garden.manager.domain.model.IrrigationResponse
import or.garden.manager.domain.model.NoteData
import or.garden.manager.domain.model.SeedData
import or.garden.manager.domain.request.CreateSeedRequest
import or.garden.manager.features.admin.AdminCropEditScreen
import or.garden.manager.features.admin.AdminCropsScreen
import or.garden.manager.features.admin.AdminPlantingMonthScreen
import or.garden.manager.features.beds.BedsScreen
import or.garden.manager.features.calendar.CalendarScreen
import or.garden.manager.features.catalog.CatalogScreen
import or.garden.manager.features.fertilizer.CreateFertilizerScreen
import or.garden.manager.features.fertilizer.NaturalFertilizerScreen
import or.garden.manager.features.irrigation.IrrigationScreen
import or.garden.manager.features.notes.NotesScreen
import or.garden.manager.features.planting.PlantingScreen
import or.garden.manager.features.seeds.SeedsScreen
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H1
import org.jetbrains.compose.web.dom.H3
import org.jetbrains.compose.web.dom.Header
import org.jetbrains.compose.web.dom.I
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

@Composable
fun AdminDashboardScreen(
    onNavigateToCrops: () -> Unit,
    onNavigateToPlantings: () -> Unit
) {
    Div({
        classes("max-w-6xl", "mx-auto")
    }) {
        H1({
            classes("text-3xl", "font-bold", "text-gray-900", "mb-10")
        }) { Text("Admin Panel") }
        Div({
            classes("grid", "grid-cols-1", "md:grid-cols-2", "lg:grid-cols-3", "gap-8")
        }) {
            AdminCard(
                title = "Upravljanje kulturama",
                description = "Dodavanje, uređivanje i brisanje biljaka iz baze podataka.",
                icon = "fa-leaf",
                color = "bg-emerald-500",
                onClick = onNavigateToCrops
            )
            AdminCard(
                title = "Kalendar sadnje",
                description = "Konfiguracija mjesečnih planova sadnje za različite regije.",
                icon = "fa-calendar-alt",
                color = "bg-blue-500",
                onClick = onNavigateToPlantings
            )
        }
    }
}

@Composable
private fun AdminCard(
    title: String,
    description: String,
    icon: String,
    color: String,
    onClick: () -> Unit
) {
    Div({
        classes(
            "bg-white",
            "rounded-2xl",
            "shadow-sm",
            "border",
            "border-gray-100",
            "p-8",
            "hover:shadow-lg",
            "transition-all",
            "cursor-pointer",
            "group"
        )
        onClick { onClick() }
    }) {
        Div({
            classes(
                color,
                "w-14",
                "h-14",
                "rounded-xl",
                "flex",
                "items-center",
                "justify-center",
                "mb-6",
                "group-hover:scale-110",
                "transition-transform"
            )
        }) {
            I({ classes("fas", icon, "text-white", "text-2xl") })
        }
        H3({
            classes("text-xl", "font-bold", "text-gray-900", "mb-3")
        }) { Text(title) }
        P({
            classes("text-gray-500", "text-sm", "leading-relaxed")
        }) { Text(description) }
        Div({
            classes("mt-6", "flex", "items-center", "text-emerald-600", "font-semibold")
        }) {
            Text("Pristupi")
            I({
                classes(
                    "fas",
                    "fa-arrow-right",
                    "ml-2",
                    "group-hover:translate-x-1",
                    "transition-transform"
                )
            })
        }
    }
}

@Composable
fun AdminPlantingsScreen(
    onBack: () -> Unit,
    onMonthClick: (Int) -> Unit
) {
    val months = listOf(
        "Januar", "Februar", "Mart", "April", "Maj", "Juni",
        "Juli", "Avgust", "Septembar", "Oktobar", "Novembar", "Decembar"
    )
    Div({
        classes("max-w-4xl", "mx-auto")
    }) {
        Button({
            classes(
                "flex",
                "items-center",
                "gap-2",
                "text-gray-500",
                "hover:text-emerald-600",
                "transition-colors",
                "mb-8"
            )
            onClick { onBack() }
        }) {
            I({ classes("fas", "fa-arrow-left") })
            Text("Nazad u Admin Panel")
        }
        Div({
            classes("grid", "grid-cols-1", "sm:grid-cols-2", "md:grid-cols-3", "gap-4")
        }) {
            months.forEachIndexed { index, month ->
                Div({
                    classes(
                        "bg-white",
                        "p-6",
                        "rounded-2xl",
                        "border",
                        "border-gray-100",
                        "shadow-sm",
                        "hover:shadow-md",
                        "hover:border-emerald-200",
                        "transition-all",
                        "cursor-pointer",
                        "flex",
                        "items-center",
                        "justify-between"
                    )
                    onClick { onMonthClick(index + 1) }
                }) {
                    Div({ classes("flex", "items-center", "gap-4") }) {
                        Div({
                            classes(
                                "w-10",
                                "h-10",
                                "rounded-lg",
                                "bg-emerald-50",
                                "text-emerald-600",
                                "flex",
                                "items-center",
                                "justify-center"
                            )
                        }) {
                            Text("${index + 1}")
                        }
                        Span({ classes("font-bold", "text-gray-900") }) { Text(month) }
                    }
                    I({ classes("fas", "fa-chevron-right", "text-gray-300") })
                }
            }
        }
    }
}

@Composable
fun Dashboard(onLogout: () -> Unit) {
    var activeTab by remember { mutableStateOf("Beds") }
    var sidebarExpanded by remember { mutableStateOf(window.innerWidth > 768) }
    var showMobileMenu by remember { mutableStateOf(false) }

    // Update sidebar state on resize
    DisposableEffect(Unit) {
        val listener = { _: org.w3c.dom.events.Event ->
            if (window.innerWidth > 768) {
                sidebarExpanded = true
                showMobileMenu = false
            } else {
                sidebarExpanded = false
            }
        }
        window.addEventListener("resize", listener)
        onDispose { window.removeEventListener("resize", listener) }
    }

    // Snackbar State
    var snackbarMessage by remember { mutableStateOf<SnackbarMessage?>(null) }
    fun showSnackbar(msg: String, type: SnackbarType = SnackbarType.INFO) {
        snackbarMessage = SnackbarMessage(msg, type)
    }

    // Global States
    var beds by remember { mutableStateOf<List<BedData>>(emptyList()) }
    var irrigationSchedules by remember { mutableStateOf<List<IrrigationResponse>>(emptyList()) }
    var crops by remember { mutableStateOf<List<CropData>>(emptyList()) }
    var favoriteCrops by remember { mutableStateOf<List<CropData>>(emptyList()) }
    var mySeeds by remember { mutableStateOf<List<SeedData>>(emptyList()) }
    var myNotes by remember { mutableStateOf<List<NoteData>>(emptyList()) }

    // Loading States
    var isLoadingBeds by remember { mutableStateOf(false) }
    var isLoadingIrrigation by remember { mutableStateOf(false) }
    var isLoadingCrops by remember { mutableStateOf(false) }
    var isLoadingSeeds by remember { mutableStateOf(false) }
    var isLoadingNotes by remember { mutableStateOf(false) }
    var isSavingSeed by remember { mutableStateOf(false) }
    var isSavingNote by remember { mutableStateOf(false) }
    var isSavingIrrigation by remember { mutableStateOf(false) }
    var isTogglingFavorite by remember { mutableStateOf(false) }

    // Triggers
    var seedAddTrigger by remember { mutableStateOf(0) }
    var noteAddTrigger by remember { mutableStateOf(0) }
    var fertilizerAddTrigger by remember { mutableStateOf(0) }

    // Search/Tab States
    var cropSearchText by remember { mutableStateOf("") }
    var showBedModal by remember { mutableStateOf(false) }
    var showIrrigationModal by remember { mutableStateOf(false) }
    var showDeleteConfirmModal by remember { mutableStateOf(false) }

    // Selection States
    var selectedBedId by remember { mutableStateOf<Int?>(null) }
    var selectedMonthIndex by remember { mutableStateOf(1) }
    var selectedCropId by remember { mutableStateOf<Int?>(null) }
    var selectedFertilizerId by remember { mutableStateOf<Int?>(null) }
    var selectedDate by remember { mutableStateOf("") }
    var selectedFrequency by remember { mutableStateOf("7") }

    var irrigationToDeleteId by remember { mutableStateOf<Int?>(null) }
    var seedToDeleteId by remember { mutableStateOf<Int?>(null) }
    var bedToDeleteId by remember { mutableStateOf<Int?>(null) }
    var noteToDeleteId by remember { mutableStateOf<Int?>(null) }

    // Bed Modal State
    var selectedBedForEdit by remember { mutableStateOf<BedData?>(null) }
    var bedName by remember { mutableStateOf("") }
    var bedWidth by remember { mutableStateOf("") }
    var bedLength by remember { mutableStateOf("") }
    var bedType by remember { mutableStateOf("Raised Bed") }
    var bedOrientation by remember { mutableStateOf("Istok-Zapad") }
    var isSavingBed by remember { mutableStateOf(false) }

    // Bed Planner State (for header buttons)
    var bedPlannerIsOpen by remember { mutableStateOf(false) }
    var bedPlannerOnSave by remember { mutableStateOf<(() -> Unit)?>(null) }
    var bedPlannerOnAddCrop by remember { mutableStateOf<(() -> Unit)?>(null) }
    var bedPlannerOnAiGenerate by remember { mutableStateOf<(() -> Unit)?>(null) }
    var bedPlannerOnDeleteAll by remember { mutableStateOf<(() -> Unit)?>(null) }
    var bedPlannerIsSaving by remember { mutableStateOf(false) }
    var bedPlannerHasPlantedCrops by remember { mutableStateOf(false) }

    // API Functions
    fun fetchBeds(force: Boolean = false) {
        if (!force && beds.isNotEmpty()) return
        isLoadingBeds = true
        GardenService.getBeds { state ->
            if (state is ResultState.Success) {
                beds = state.data.data ?: emptyList()
                isLoadingBeds = false
            } else if (state is ResultState.Error) {
                isLoadingBeds = false
            }
        }
    }

    fun fetchIrrigation(force: Boolean = false) {
        if (!force && irrigationSchedules.isNotEmpty()) return
        isLoadingIrrigation = true
        IrrigationService.getIrrigation { state ->
            if (state is ResultState.Success) {
                irrigationSchedules = state.data.data ?: emptyList()
                isLoadingIrrigation = false
            } else if (state is ResultState.Error) {
                isLoadingIrrigation = false
            }
        }
    }

    fun fetchCrops(query: String = "", force: Boolean = false) {
        if (!force && query.isEmpty() && crops.isNotEmpty()) return
        isLoadingCrops = true
        CropService.getCrops(query) { state ->
            if (state is ResultState.Success) {
                crops = state.data.data ?: emptyList()
                favoriteCrops = crops.filter { it.isFavourite }
                isLoadingCrops = false
            } else if (state is ResultState.Error) {
                isLoadingCrops = false
            }
        }
    }

    fun fetchSeeds(force: Boolean = false) {
        if (!force && mySeeds.isNotEmpty()) return
        isLoadingSeeds = true
        SeedService.getSeeds { state ->
            if (state is ResultState.Success) {
                mySeeds = state.data.data ?: emptyList()
                isLoadingSeeds = false
            } else if (state is ResultState.Error) {
                isLoadingSeeds = false
            }
        }
    }

    fun fetchNotes(force: Boolean = false) {
        if (!force && myNotes.isNotEmpty()) return
        isLoadingNotes = true
        NoteService.getNotes { state ->
            if (state is ResultState.Success) {
                myNotes = state.data.data ?: emptyList()
                isLoadingNotes = false
            } else if (state is ResultState.Error) {
                isLoadingNotes = false
            }
        }
    }

    // Lifecycle
    LaunchedEffect(activeTab) {
        when (activeTab) {
            "Beds" -> {
                fetchBeds()
                fetchCrops() // Potrebno za planer leja
            }

            "Irrigation" -> fetchIrrigation()
            "Database", "Calendar" -> fetchCrops()
            "MySeeds" -> fetchSeeds()
            "Notes" -> fetchNotes()
            "AdminCrops" -> fetchCrops(force = true)
            "NaturalFertilizers" -> { /* Handled in screen */
            }
        }
    }

    // Actions
    fun saveBed() {
        if (bedName.isBlank() || bedWidth.isBlank() || bedLength.isBlank()) return
        isSavingBed = true
        val width = bedWidth.toIntOrNull() ?: 0
        val length = bedLength.toIntOrNull() ?: 0
        val bedData = BedData(
            id = selectedBedForEdit?.id ?: 0,
            userId = 0,
            name = bedName,
            width = width,
            length = length,
            type = bedType,
            orientation = bedOrientation
        )

        val onResult: (ResultState<ApiResponse<BedData>>) -> Unit = { state ->
            when (state) {
                is ResultState.Success -> {
                    isSavingBed = false
                    showBedModal = false
                    fetchBeds(force = true)
                    showSnackbar("Leja uspješno sačuvana", SnackbarType.SUCCESS)
                }

                is ResultState.Error -> {
                    isSavingBed = false
                    showSnackbar("Greška: ${state.message}", SnackbarType.ERROR)
                }

                else -> {}
            }
        }

        if (selectedBedForEdit == null) GardenService.addBed(bedData, onResult)
        else GardenService.updateBed(bedData, onResult)
    }

    fun saveIrrigation() {
        val bedId = selectedBedId ?: return
        if (selectedDate.isBlank()) return
        isSavingIrrigation = true
        val request = CreateIrrigationRequest(
            bedId = bedId,
            nextIrrigationDate = "${selectedDate}T10:00:00Z",
            frequency = selectedFrequency
        )
        IrrigationService.createIrrigation(request) { state ->
            if (state is ResultState.Success) {
                isSavingIrrigation = false
                showIrrigationModal = false
                fetchIrrigation(force = true)
                showSnackbar("Navodnjavanje zakazano", SnackbarType.SUCCESS)
            } else if (state is ResultState.Error) {
                isSavingIrrigation = false
                showSnackbar("Greška: ${state.message}", SnackbarType.ERROR)
            }
        }
    }

    fun toggleFavorite(crop: CropData) {
        isTogglingFavorite = true
        val onResult: (ResultState<Unit>) -> Unit = { state ->
            if (state is ResultState.Success) {
                fetchCrops(cropSearchText, force = true)
            }
            isTogglingFavorite = false
        }
        if (crop.isFavourite) CropService.removeFavourite(crop.id, onResult)
        else CropService.addFavourite(crop.id, onResult)
    }

    fun handleSaveSeed(
        selectedSeed: SeedData?, name: String, variety: String, type: String,
        harvestTime: String, months: Set<Int>, isHybrid: Boolean,
        photo: ByteArray?, backPhoto: ByteArray?, onSuccess: () -> Unit
    ) {
        isSavingSeed = true
        val seedlingDate = if (months.isEmpty()) null else months.sorted().joinToString(",")
        val harvest = harvestTime.toIntOrNull()
        val onResult: (ResultState<ApiResponse<SeedData>>) -> Unit = { state ->
            when (state) {
                is ResultState.Success -> {
                    isSavingSeed = false
                    onSuccess()
                    fetchSeeds(force = true)
                    showSnackbar("Sjemenka uspješno sačuvana", SnackbarType.SUCCESS)
                }

                is ResultState.Error -> {
                    isSavingSeed = false
                    showSnackbar(
                        "Greška pri spremanju sjemenke: ${state.message}",
                        SnackbarType.ERROR
                    )
                }

                else -> {}
            }
        }
        if (selectedSeed == null) {
            if (photo != null || backPhoto != null) {
                SeedService.createSeedMultipart(
                    name,
                    variety,
                    isHybrid,
                    photo,
                    backPhoto,
                    type,
                    harvest,
                    seedlingDate,
                    onResult
                )
            } else {
                SeedService.createSeed(
                    CreateSeedRequest(
                        name = name,
                        latinName = variety,
                        hybrid = isHybrid,
                        type = type,
                        harvestTime = harvest,
                        seedlingDate = seedlingDate
                    ), onResult
                )
            }
        } else {
            if (photo != null || backPhoto != null) {
                SeedService.updateSeedMultipart(
                    selectedSeed.id,
                    name,
                    variety,
                    isHybrid,
                    photo,
                    backPhoto,
                    type,
                    harvest,
                    seedlingDate,
                    onResult
                )
            } else {
                SeedService.updateSeed(
                    selectedSeed.id,
                    CreateSeedRequest(
                        name = name,
                        latinName = variety,
                        hybrid = isHybrid,
                        type = type,
                        harvestTime = harvest,
                        seedlingDate = seedlingDate
                    ),
                    onResult
                )
            }
        }
    }

    fun handleSaveNote(
        selectedNote: NoteData?, title: String, content: String, onSuccess: () -> Unit
    ) {
        isSavingNote = true
        val onResult: (ResultState<ApiResponse<NoteData>>) -> Unit = { state ->
            when (state) {
                is ResultState.Success -> {
                    isSavingNote = false
                    onSuccess()
                    fetchNotes(force = true)
                    showSnackbar("Bilješka uspješno sačuvana", SnackbarType.SUCCESS)
                }

                is ResultState.Error -> {
                    isSavingNote = false
                    showSnackbar(
                        "Greška pri spremanju bilješke: ${state.message}",
                        SnackbarType.ERROR
                    )
                }

                else -> {}
            }
        }
        if (selectedNote == null) {
            NoteService.createNote(title, content, onResult)
        } else {
            NoteService.updateNote(selectedNote.id, title, content, onResult)
        }
    }

    Div({
        classes("flex", "flex-col", "md:flex-row", "h-screen", "bg-gray-50", "overflow-hidden")
    }) {
        // Desktop Sidebar
        Div({ classes("hidden", "md:block") }) {
            Sidebar(
                activeTab = activeTab,
                expanded = sidebarExpanded,
                onTabChange = { activeTab = it },
                onLogout = onLogout
            )
        }

        // Mobile Sidebar / Menu
        if (showMobileMenu) {
            Div({
                classes("fixed", "inset-0", "z-50", "md:hidden")
            }) {
                // Backdrop
                Div({
                    classes("absolute", "inset-0", "bg-black/50")
                    onClick { showMobileMenu = false }
                })
                // Content
                Div({
                    classes(
                        "absolute",
                        "left-0",
                        "top-0",
                        "bottom-0",
                        "w-72",
                        "bg-[#134130]",
                        "shadow-xl"
                    )
                }) {
                    Sidebar(
                        activeTab = activeTab,
                        expanded = true,
                        onTabChange = {
                            activeTab = it
                            showMobileMenu = false
                        },
                        onLogout = onLogout
                    )
                }
            }
        }

        Div({ classes("flex-1", "flex", "flex-col", "overflow-hidden") }) {
            // Header
            Header({
                classes(
                    "bg-white",
                    "border-b",
                    "border-gray-200",
                    "px-4",
                    "md:px-8",
                    "py-6",
                    "md:py-10",
                    "flex",
                    "justify-between",
                    "items-center",
                    "md:items-start"
                )
            }) {
                Div({ classes("flex", "items-center", "gap-4") }) {
                    // Mobile Menu Toggle
                    Button({
                        classes(
                            "md:hidden",
                            "text-gray-500",
                            "p-2",
                            "hover:bg-gray-100",
                            "rounded-lg"
                        )
                        onClick { showMobileMenu = !showMobileMenu }
                    }) {
                        I({
                            classes(
                                "fas",
                                if (showMobileMenu) "fa-times" else "fa-bars",
                                "text-2xl"
                            )
                        })
                    }

                    Div {
                        H1({ classes("text-2xl", "md:text-4xl", "font-bold", "text-[#134130]") }) {
                            Text(
                                when (activeTab) {
                                    "Beds" -> "Moje leje"
                                    "Irrigation" -> "Navodnjavanje"
                                    "Database" -> "Katalog kultura"
                                    "MySeeds" -> "Moje sjemenke"
                                    "Notes" -> "Bilješke"
                                    "Planting" -> "Sadnja - Ovaj mjesec"
                                    "Calendar" -> "Kalendar"
                                    "NaturalFertilizers" -> "Prirodna đubriva"
                                    "NaturalFertilizerEdit" -> "Upravljanje đubrivom"
                                    "Admin" -> "Admin Panel"
                                    "AdminCrops", "AdminCropEdit" -> "Upravljanje kulturama"
                                    "AdminPlantings", "AdminPlantingMonth" -> "Kalendar sadnje"
                                    else -> activeTab
                                }
                            )
                        }
                    }
                }

                val userRole = GardenHttpClient.role
                val isAdmin = userRole == "ADMIN"

                // Show bed planner buttons when planner is open, otherwise show regular "Dodaj" button
                if (bedPlannerIsOpen && activeTab == "Beds") {
                    // Bed Planner Buttons
                    Div({ classes("flex", "gap-2", "md:gap-3") }) {
                        // Delete All Button
                        if (bedPlannerOnDeleteAll != null && bedPlannerHasPlantedCrops) {
                            Button(attrs = {
                                classes(
                                    "w-10",
                                    "md:w-12",
                                    "h-10",
                                    "md:h-12",
                                    "bg-white",
                                    "text-red-600",
                                    "border-2",
                                    "border-red-100",
                                    "rounded-xl",
                                    "font-bold",
                                    "flex",
                                    "items-center",
                                    "justify-center",
                                    "hover:bg-red-50",
                                    "transition-all"
                                )
                                onClick { bedPlannerOnDeleteAll?.invoke() }
                            }) {
                                I({ classes("fas", "fa-trash") })
                            }
                        }

                        // AI Button
                        if (bedPlannerOnAiGenerate != null) {
                            Button(attrs = {
                                classes(
                                    "px-3",
                                    "md:px-4",
                                    "py-2",
                                    "md:py-3",
                                    "bg-white",
                                    "text-purple-600",
                                    "border-2",
                                    "border-purple-200",
                                    "rounded-xl",
                                    "font-bold",
                                    "flex",
                                    "items-center",
                                    "gap-2",
                                    "hover:bg-purple-50",
                                    "transition-all",
                                    "text-sm",
                                    "md:text-base"
                                )
                                onClick { bedPlannerOnAiGenerate?.invoke() }
                            }) {
                                I({ classes("fas", "fa-robot") })
                                Span({ classes("hidden", "sm:inline") }) { Text("AI") }
                            }
                        }

                        // Add Crop Button
                        if (bedPlannerOnAddCrop != null) {
                            Button(attrs = {
                                classes(
                                    "px-3",
                                    "md:px-4",
                                    "py-2",
                                    "md:py-3",
                                    "bg-white",
                                    "text-emerald-600",
                                    "border",
                                    "border-emerald-600",
                                    "rounded-xl",
                                    "font-bold",
                                    "flex",
                                    "items-center",
                                    "gap-2",
                                    "hover:bg-emerald-50",
                                    "transition-all",
                                    "text-sm",
                                    "md:text-base"
                                )
                                onClick { bedPlannerOnAddCrop?.invoke() }
                            }) {
                                I({ classes("fas", "fa-plus") })
                                Span({ classes("hidden", "sm:inline") }) { Text("Dodaj kulturu") }
                            }
                        }

                        // Save Button
                        if (bedPlannerOnSave != null) {
                            Button(attrs = {
                                classes(
                                    "px-3",
                                    "md:px-4",
                                    "py-2",
                                    "md:py-3",
                                    "bg-emerald-600",
                                    "text-white",
                                    "rounded-xl",
                                    "font-bold",
                                    "flex",
                                    "items-center",
                                    "gap-2",
                                    "transition-all",
                                    "text-sm",
                                    "md:text-base"
                                )
                                if (bedPlannerIsSaving) {
                                    attr("disabled", "true")
                                }
                                onClick { bedPlannerOnSave?.invoke() }
                            }) {
                                if (bedPlannerIsSaving) I({ classes("fas", "fa-circle-notch", "fa-spin") })
                                else I({ classes("fas", "fa-save") })
                                Span({ classes("hidden", "sm:inline") }) {
                                    Text(if (bedPlannerIsSaving) "Spremanje..." else "Sačuvaj")
                                }
                            }
                        }
                    }
                } else if ((isAdmin || activeTab == "Beds" || activeTab == "Irrigation" || activeTab == "NaturalFertilizers" || activeTab == "MySeeds" || activeTab == "Notes") &&
                    activeTab != "Database" && activeTab != "Calendar" && activeTab != "Admin" && activeTab != "Planting" && !activeTab.startsWith("Admin")
                ) {
                    val buttonColorClass = if (activeTab == "Irrigation") "bg-blue-500" else "bg-[#10B981]"
                    
                    Button(attrs = {
                        classes(
                            buttonColorClass,
                            "text-white",
                            "px-4",
                            "md:px-6",
                            "py-2",
                            "md:py-3",
                            "rounded-xl",
                            "flex",
                            "items-center",
                            "gap-2",
                            "font-bold",
                            "text-sm",
                            "md:text-base"
                        )
                        onClick {
                            when (activeTab) {
                                "Irrigation" -> showIrrigationModal = true
                                "Beds" -> {
                                    selectedBedForEdit = null
                                    bedName = ""
                                    bedWidth = ""
                                    bedLength = ""
                                    bedType = "Raised Bed"
                                    bedOrientation = "Istok-Zapad"
                                    showBedModal = true
                                }

                                "MySeeds" -> {
                                    seedAddTrigger++
                                }

                                "Notes" -> {
                                    noteAddTrigger++
                                }

                                "NaturalFertilizers" -> {
                                    selectedFertilizerId = null
                                    activeTab = "NaturalFertilizerEdit"
                                }
                            }
                        }
                    }) {
                        I({ classes("fas", "fa-plus") })
                        Text("Dodaj")
                    }
                }
            }
            Div({ classes("flex-1", "overflow-y-auto", "p-4", "md:p-8") }) {
                when (activeTab) {
                    "Beds" -> BedsScreen(
                        beds = beds,
                        isLoadingBeds = isLoadingBeds,
                        crops = crops,
                        onRefreshBeds = { fetchBeds(force = true) },
                        onEditBed = { bed ->
                            selectedBedForEdit = bed
                            bedName = bed.name
                            bedType = bed.type
                            bedWidth = bed.width.toString()
                            bedLength = bed.length.toString()
                            bedOrientation = bed.orientation
                            showBedModal = true
                        },
                        onDeleteBed = { id ->
                            bedToDeleteId = id
                            showDeleteConfirmModal = true
                        },
                        showSnackbar = { m, t ->
                            showSnackbar(m, t)
                        },
                        onPlannerStateChange = { isOpen, onSave, onAddCrop, onAiGenerate, onDeleteAll, isSaving, hasPlantedCrops ->
                            bedPlannerIsOpen = isOpen
                            bedPlannerOnSave = onSave
                            bedPlannerOnAddCrop = onAddCrop
                            bedPlannerOnAiGenerate = onAiGenerate
                            bedPlannerOnDeleteAll = onDeleteAll
                            bedPlannerIsSaving = isSaving
                            bedPlannerHasPlantedCrops = hasPlantedCrops
                        }
                    )

                    "Irrigation" -> IrrigationScreen(
                        isLoadingIrrigation,
                        irrigationSchedules,
                        { id ->
                            IrrigationService.markAsWatered(id) { state ->
                                if (state is ResultState.Success) fetchIrrigation(force = true)
                            }
                        }) { id ->
                        irrigationToDeleteId = id; showDeleteConfirmModal = true
                    }

                    "Database" -> CatalogScreen(
                        crops,
                        favoriteCrops,
                        isLoadingCrops,
                        isTogglingFavorite,
                        cropSearchText,
                        { cropSearchText = it; fetchCrops(it, force = true) },
                        ::toggleFavorite
                    )

                    "Calendar" -> CalendarScreen(crops) { /* Handle crop click */ }
                    "Planting" -> PlantingScreen()
                    "MySeeds" -> SeedsScreen(
                        mySeeds,
                        isLoadingSeeds,
                        isSavingSeed,
                        seedAddTrigger,
                        ::handleSaveSeed
                    ) { id ->
                        seedToDeleteId = id; showDeleteConfirmModal = true
                    }

                    "Notes" -> NotesScreen(
                        myNotes,
                        isLoadingNotes,
                        isSavingNote,
                        noteAddTrigger,
                        ::handleSaveNote
                    ) { id ->
                        noteToDeleteId = id; showDeleteConfirmModal = true
                    }

                    "Admin" -> AdminDashboardScreen(
                        onNavigateToCrops = { activeTab = "AdminCrops" },
                        onNavigateToPlantings = { activeTab = "AdminPlantings" }
                    )

                    "AdminCrops" -> AdminCropsScreen(
                        onBack = { activeTab = "Admin" },
                        onEditCrop = { id ->
                            selectedCropId = id
                            activeTab = "AdminCropEdit"
                        }
                    )

                    "AdminCropEdit" -> AdminCropEditScreen(
                        cropId = selectedCropId,
                        onBack = { activeTab = "AdminCrops" }
                    )

                    "AdminPlantings" -> AdminPlantingsScreen(
                        onBack = { activeTab = "Admin" },
                        onMonthClick = { monthIndex ->
                            selectedMonthIndex = monthIndex
                            activeTab = "AdminPlantingMonth"
                        }
                    )

                    "AdminPlantingMonth" -> AdminPlantingMonthScreen(
                        monthNumber = selectedMonthIndex,
                        onBack = { activeTab = "AdminPlantings" },
                        showSnackbar = { m, t -> showSnackbar(m, t) }
                    )

                    "NaturalFertilizers" -> NaturalFertilizerScreen(
                        onAdd = {
                            selectedFertilizerId = null
                            activeTab = "NaturalFertilizerEdit"
                        },
                        onEdit = { id ->
                            selectedFertilizerId = id
                            activeTab = "NaturalFertilizerEdit"
                        },
                        showSnackbar = { m, t -> showSnackbar(m, t) }
                    )

                    "NaturalFertilizerEdit" -> CreateFertilizerScreen(
                        fertilizerId = selectedFertilizerId,
                        onBack = { activeTab = "NaturalFertilizers" },
                        onSuccess = {
                            activeTab = "NaturalFertilizers"
                            showSnackbar("Uspješno sačuvano!", SnackbarType.SUCCESS)
                        }
                    )
                }
            }
        }

        // Modals
        if (showIrrigationModal) IrrigationModal(
            beds,
            selectedBedId,
            { selectedBedId = it },
            selectedDate,
            { selectedDate = it },
            selectedFrequency,
            { selectedFrequency = it },
            isSavingIrrigation,
            ::saveIrrigation
        ) { showIrrigationModal = false }
        if (showBedModal) BedModal(
            selectedBedForEdit,
            bedName,
            { bedName = it },
            bedType,
            { bedType = it },
            bedWidth,
            { bedWidth = it },
            bedLength,
            { bedLength = it },
            bedOrientation,
            { bedOrientation = it },
            isSavingBed,
            ::saveBed
        ) { showBedModal = false }

        if (showDeleteConfirmModal) {
            ConfirmDeleteModal("Potvrda brisanja", "Da li ste sigurni?", {
                irrigationToDeleteId?.let { id ->
                    IrrigationService.deleteIrrigation(id) { state ->
                        if (state is ResultState.Success) {
                            fetchIrrigation(force = true)
                            showSnackbar("Obrisano uspješno", SnackbarType.SUCCESS)
                        }
                    }
                }
                seedToDeleteId?.let { id ->
                    SeedService.deleteSeed(id) { state ->
                        if (state is ResultState.Success) {
                            fetchSeeds(force = true)
                            showSnackbar("Sjemenka obrisana", SnackbarType.SUCCESS)
                        }
                    }
                }
                bedToDeleteId?.let { id ->
                    GardenService.deleteBed(id) { state ->
                        if (state is ResultState.Success) {
                            fetchBeds(force = true)
                            showSnackbar("Leja obrisana", SnackbarType.SUCCESS)
                        }
                    }
                }
                noteToDeleteId?.let { id ->
                    NoteService.deleteNote(id) { state ->
                        if (state is ResultState.Success) {
                            fetchNotes(force = true)
                            showSnackbar("Bilješka obrisana", SnackbarType.SUCCESS)
                        }
                    }
                }
                showDeleteConfirmModal = false
            }) {
                irrigationToDeleteId = null
                seedToDeleteId = null
                bedToDeleteId = null
                noteToDeleteId = null
                showDeleteConfirmModal = false
            }
        }

        SnackbarHost(snackbarMessage) {
            snackbarMessage = null
        }
    }
}
